import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Map, Marker, InfoWindow, GoogleApiWrapper } from "google-maps-react";
import { useSelector, useDispatch } from "react-redux";
import { CaretUpFilled, CaretDownFilled } from "@ant-design/icons";
import { setMapOnReady } from "../../../redux/actions/PPT";
import defaultViewStyle, {
  darkThemeViewStyle,
  defaultMapStyle,
  defaultMapStyles,
  lightThemeViewStyle,
} from "./MapFeatureViewStyles";
import { globalChartColor } from "../../../redux/constants/enums";
import { newColorShadeInHex6 } from "../../../redux/constants";
import "./GoogleMultiLayerMap.scss";
import { Tooltip } from "antd";
const MapContainer = ({
  mapData,
  google,
  preview,
  region = "IN",
  createdWidget,
  PdfDownloadStatus,
  noteExist,
  insightsExist,
  widgetDiscription,
  widget_id,
  language,
  widgetDurationShowPercentChange,
  widgetDurationShowData,
}) => {
  const dispatch = useDispatch();
  const mapRender = useSelector((state) => state?.PPT?.mapRender);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [mapInitializationStatus, setMapInitializationStatus] = useState(false);
  const [breakdownLegends, setBreakdownLegends] = useState(null);
  const [thresholdLegends, setThresholdLegends] = useState(null);
  // legendContainer ref
  const legendContainerRef = useRef(null);
  const [refUpdated, setRefUpdated] = useState(false);
  const [legendContainerHeightInPx, setLegendContainerHeightInPx] =
    useState("0px");
  // Throttled function to update size
  const updateSize = useCallback(() => {
    let legendContainerHeightInPx;
    if (legendContainerRef?.current) {
      const legendContainerHeight =
        legendContainerRef?.current?.clientHeight || 0;
      legendContainerHeightInPx = `${legendContainerHeight}px`;
      setLegendContainerHeightInPx(legendContainerHeightInPx);
    }
  }, [legendContainerRef, legendContainerRef?.current]);
  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      // Batch size updates to avoid triggering multiple updates in a single loop
      requestAnimationFrame(updateSize);
    });
    if (legendContainerRef?.current) {
      resizeObserver.observe(legendContainerRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [updateSize]);

  useEffect(() => {
    let timer;
    if (mapInitializationStatus) {
      timer = setTimeout(() => {
        dispatch(setMapOnReady(true));
      }, 7000);
    }
    return () => clearTimeout(timer);
  }, [mapInitializationStatus]);
  const getThresholdLegendByColor = (color, thresholdLegends) => {
    if (!color || !thresholdLegends || typeof thresholdLegends !== "object")
      return null;
    return Object.values(thresholdLegends).find(
      (rating) => rating?.color === color
    );
  };
  // preparing locations data when mapData is changed
  const locationsData = useMemo(() => {
    let rawMapData = mapData?.[0]?.latitude
      ? mapData
      : mapData?.[0]?.data?.[0]?.latitude
      ? mapData?.[0]?.data
      : mapData?.data?.[0]?.latitude
      ? mapData?.data
      : [];
    let rawBreakdownData = mapData?.y
      ? mapData?.y
      : mapData?.data?.y
      ? mapData?.data?.y
      : mapData?.[0]?.y
      ? mapData?.[0]?.y
      : [];

    let selectedColorArray = globalChartColor;
    // For calculating breakdownLegend
    // for location breakdown data
    let jBreakdown = 0;
    let noOfIterationInColorsBreakdown = 0;
    let lengthOfColors = selectedColorArray?.length;
    rawBreakdownData = Array.isArray(rawBreakdownData)
      ? rawBreakdownData?.map((breakBY, breakIndex) => {
          let breakdownColor;
          if (
            breakBY?.color !== undefined &&
            breakBY?.color !== "#000000" &&
            breakBY?.color !== null
          ) {
            breakdownColor = breakBY?.color;
          } else {
            if (breakIndex >= lengthOfColors) {
              noOfIterationInColorsBreakdown = parseInt(
                breakIndex / lengthOfColors
              );
              let rem = parseInt(breakIndex % lengthOfColors);
              breakdownColor =
                noOfIterationInColorsBreakdown > 0
                  ? newColorShadeInHex6(
                      selectedColorArray[rem],
                      parseInt(noOfIterationInColorsBreakdown * 5)
                    )
                  : selectedColorArray[breakIndex];
            } else {
              breakdownColor = selectedColorArray[breakIndex];
            }
          }
          return { ...breakBY, color: breakdownColor };
        })
      : rawBreakdownData;
    if (rawBreakdownData) setBreakdownLegends(rawBreakdownData);
    // For calculating thresholdLegend
    let thresholdLegends = mapData?.threshold_range
      ? mapData?.threshold_range
      : mapData?.data?.threshold_range
      ? mapData?.data?.threshold_range
      : mapData?.[0]?.threshold_range
      ? mapData?.[0]?.threshold_range
      : null;

    if (thresholdLegends) setThresholdLegends(thresholdLegends);

    // for location color
    let j = 0;
    let noOfIterationInColors = 0;
    noOfIterationInColorsBreakdown = 0;
    lengthOfColors = selectedColorArray?.length;
    if (Array.isArray(rawMapData)) {
      const updatedMapData = rawMapData?.map((loc, locIndex) => {
        let locationColor;
        if (
          loc?.color !== undefined &&
          loc?.color !== "#000000" &&
          loc?.color !== null
        ) {
          locationColor = loc?.color;
        } else {
          if (j >= lengthOfColors) {
            noOfIterationInColors = parseInt(j / lengthOfColors);
            let rem = parseInt(j % lengthOfColors);
            locationColor =
              noOfIterationInColors > 0
                ? newColorShadeInHex6(
                    selectedColorArray[rem],
                    parseInt(noOfIterationInColors * 5)
                  )
                : selectedColorArray[j];
          } else {
            locationColor = selectedColorArray[j];
          }
          j++;
        }

        // For Breakdown Color
        let breakdownObj = Array.isArray(rawBreakdownData)
          ? rawBreakdownData?.find(
              (breakBY) => breakBY?.name === loc?.breakdown_val
            )
          : null;
        let breakdownColor = breakdownObj?.color
          ? breakdownObj?.color
          : loc?.breakdown_color;
        let thresholdLegendOfLoc;
        // For thresholdLegend
        if (thresholdLegends) {
          thresholdLegendOfLoc = getThresholdLegendByColor(
            locationColor,
            thresholdLegends
          );
        }
        return {
          id: `${widget_id}_LocationID_${loc?.id}_Index_${locIndex}`,
          name: loc?.name,
          coordinates: { lat: loc?.latitude, lng: loc?.longitude },
          current_metric: loc?.current_metric,
          previous_metric: loc?.previous_metric,
          size: loc?.size,
          previous_size: loc?.previous_size,
          color: locationColor,
          breakdown_val: loc?.breakdown_val,
          breakdown_color: breakdownColor,
          thresholdLegend: thresholdLegendOfLoc,
          series_metrics: loc?.series_metrics,
        };
      });
      return updatedMapData;
    } else {
      return [];
    }
  }, [mapData]);
  const center = useMemo(() => {
    return {
      lat: locationsData?.[0]?.coordinates?.lat,
      lng: locationsData?.[0]?.coordinates?.lng,
    };
  }, [locationsData]);
  const mapOptions = {
    geopoliticsView: "IN", // Ensures official Indian boundaries
  };
  const mapStyle = useMemo(() => {
    // Define the map's style.
    let mapStyle = {
      position: "relative",
      marginLeft: preview || createdWidget ? "" : "-1.4rem",
      width: preview ? "auto" : "100%",
      height: preview
        ? `calc(100vh - 19.2rem - ${legendContainerHeightInPx})`
        : PdfDownloadStatus
        ? noteExist
          ? widgetDiscription && widgetDiscription?.length
            ? widgetDiscription?.length > 130
              ? `calc(46rem - ${legendContainerHeightInPx})`
              : `calc(48rem - ${legendContainerHeightInPx})`
            : `calc(50rem - ${legendContainerHeightInPx})`
          : widgetDiscription && widgetDiscription?.length
          ? widgetDiscription?.length > 130
            ? `calc(64.5rem - ${legendContainerHeightInPx})`
            : `calc(66.5rem - ${legendContainerHeightInPx})`
          : `calc(68.5rem - ${legendContainerHeightInPx})`
        : createdWidget
        ? `calc(100vh - 16.5rem - ${legendContainerHeightInPx}) `
        : `calc(70% - ${legendContainerHeightInPx})`,
    };
    return mapStyle;
  }, [
    preview,
    createdWidget,
    PdfDownloadStatus,
    noteExist,
    widgetDiscription,
    legendContainerHeightInPx,
  ]);
  //function for marker colors
  const getFillColor = (location) => {
    return location?.color;
  };

  //function for border color based on breakdown value
  const getBorderColor = (location) => {
    return location?.breakdown_val && location?.breakdown_color
      ? location?.breakdown_color
      : location?.color;
  };

  // Function to create custom SVG marker
  const getCustomMarker = useCallback((location) => {
    if (!location) return;
    const fillColor = getFillColor(location);
    const borderColor = getBorderColor(location);
    // const size = Math.sqrt(location?.size) * 4 + 20; // Adjust the scaling factor
    const rawSize = Math.sqrt(location?.size || 0) * 4 + 20;
    const minSize = 20; // Minimum size for the marker
    const maxSize = 100; // Maximum size for the marker
    const size = Math.max(minSize, Math.min(rawSize, maxSize)); // Adjust the scaling factor
    const fillOpacity = 0.7; // Adjust fill opacity
    const strokeOpacity = 1; // Adjust stroke opacity
    const strokeBorderWidth = location?.breakdown_val ? 3 : 2; // Adjust stroke border width
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
          <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
            <circle cx="${size / 2}" cy="${size / 2}" r="${
        size / 4
      }"  fill="${fillColor}" fill-opacity="${fillOpacity}"  stroke="${borderColor}" stroke-opacity="${strokeOpacity}"  stroke-width="${strokeBorderWidth}"/>
          </svg>
        `)}`,
      scaledSize: new window.google.maps.Size(size, size),
      anchor: new window.google.maps.Point(size / 2, size / 2), // Center the marker
    };
  }, []);
  const checkBackgroundIsDark = (bgColor) => {
    const color = bgColor?.substring(1); // remove the '#'
    const rgb = parseInt(color, 16); // convert rrggbb to decimal
    const r = (rgb >> 16) & 0xff; // extract red
    const g = (rgb >> 8) & 0xff; // extract green
    const b = (rgb >> 0) & 0xff; // extract blue

    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b; // calculate luma
    return luma > 160 ? false : true; // return true for dark backgrounds and false for light backgrounds
  };
  const gradient_old = [
    "rgba(0, 255, 255, 0)",
    "blue",
    "green",
    "green",
    "green",
    "green",
    "green",
    "green",
    "red",
    "red",
    "red",
    "red",
    "red",
    "red",
  ];
  // Close InfoWindow when clicking outside InfoWindow (on the map)
  const onMapClick = () => {
    if (selectedLocation) {
      setSelectedLocation(null);
    }
  };
  // When the map is ready, you can perform additional actions.
  const onReady = (mapProps, map) => {
    setMapInitializationStatus(true);
  };
  // console.log(
  //   "mapData : ",
  //   mapData,
  //   "locationsData : ",
  //   locationsData,
  //   "center : ",
  //   center,
  //   "breakdownLegends : ",
  //   breakdownLegends,
  //   "thresholdLegends : ",
  //   thresholdLegends,
  //   "mapStyle : ",
  //   mapStyle
  // );
  if (!google) return <div>Loading map...</div>; // Ensure google is available
  return (
    <>
      <div
        style={{
          height: `calc(${mapStyle?.height} + ${legendContainerHeightInPx}`,
        }}
        className={`${PdfDownloadStatus && noteExist ? "mb-10" : ""} ${
          PdfDownloadStatus ? "m-10" : ""
        }`}
        id={`GoogleMultiLayerMap_${widget_id}`}
      >
        <div
          className="GoogleMultiLayerMap_Container"
          style={{ height: mapStyle?.height }}
        >
          <Map
            google={google}
            className={`map ${createdWidget && "position-relative"}`}
            zoom={5}
            region={region}
            initialCenter={
              center?.lat ? center : { lat: 19.07609, lng: 72.877426 }
            }
            onReady={(mapProps, map) => onReady(mapProps, map)}
            style={mapStyle}
            zoomControl={!PdfDownloadStatus}
            mapTypeControl={false}
            scaleControl={false}
            streetViewControl={false}
            rotateControl={false}
            panControl={true}
            fullscreenControl={!PdfDownloadStatus}
            zoomControlOptions={{
              position: google?.maps?.ControlPosition?.LEFT_BOTTOM,
            }}
            fullscreenControlOptions={{
              position: google?.maps?.ControlPosition?.LEFT_BOTTOM,
            }}
            mapTypeControlOptions={null}
            /**
             * gestureHandling controls how the user can interact with the map
             *
             * Available values:
             * - "auto": Default behavior. Dragging/gestures enabled on desktop,
             *           two-finger requirement on mobile inside scrollable pages
             * - "greedy": All touch gestures and scroll interactions enabled.
             *             Users can scroll and zoom without restrictions
             * - "cooperative": Map only pans/zooms with two fingers (touch) or
             *                  Ctrl/⌘ key + scroll (desktop). Prevents accidental movement (CURRENT SETTING)
             * - "none": All interactions disabled. Map becomes non-interactive
             *
             * Using "cooperative" for better user experience - prevents accidental map movement
             * when scrolling through the page, requires deliberate two-finger gestures on mobile
             * or Ctrl/⌘ key + scroll on desktop to interact with the map
             */
            gestureHandling={"cooperative"}
            // styles={defaultViewStyle}
            // styles={darkThemeViewStyle}
            // styles={lightThemeViewStyle}
            styles={defaultMapStyle}
            onClick={onMapClick} // Detect clicks outside InfoWindow
          >
            {locationsData &&
              Array.isArray(locationsData) &&
              locationsData?.map((location) => (
                <Marker
                  key={location?.id}
                  position={location?.coordinates}
                  icon={getCustomMarker(location)}
                  // onClick={() => setSelectedLocation(location)}
                  onClick={(props, marker) => setSelectedLocation(location)}
                />
              ))}
            <InfoWindow
              className="map-custom-info-window"
              position={selectedLocation?.coordinates}
              visible={selectedLocation ? true : false}
              onClose={() => setSelectedLocation(null)} // Close on clicking "X"
            >
              <div className="info-window-content">
                <div className="info-header">
                  <span className="info-title">{selectedLocation?.name}</span>
                  {/* <span
                  className="info-close-btn"
                  onClick={(e) => {
                    console.log("Button click got called ", e);
                    setSelectedLocation(null);
                    e?.stopPropagation(); // Prevent event bubbling
                  }} // Close on clicking "X"
                  aria-label="Close Info Window"
                >
                  &times; X
                </span> */}
                </div>
                <div className="info-body">
                  {/* Show Series Metrics Info Start Here */}
                  {selectedLocation?.series_metrics &&
                  Array.isArray(selectedLocation?.series_metrics) ? (
                    <>
                      {selectedLocation?.series_metrics?.map(
                        (seriesMetrics) => {
                          let fillColor = getFillColor(selectedLocation);
                          let borderColor = getBorderColor(selectedLocation);
                          return (
                            <>
                              <div className="info-section">
                                <span className="info-label">{`${
                                  seriesMetrics?.trace_name
                                    ? seriesMetrics?.trace_name
                                    : seriesMetrics?.attribute
                                }: `}</span>
                                <span className="info-value-container">
                                  <span
                                    className="info-value-box"
                                    // className={`info-value-box
                                    //   ${
                                    //     checkBackgroundIsDark(fillColor)
                                    //       ? "info-dark-bg"
                                    //       : ""
                                    //   }
                                    // `}
                                    style={
                                      {
                                        //   background: `linear-gradient(
                                        //   45deg,
                                        //   ${fillColor},
                                        //   ${fillColor}60
                                        // )`,
                                        // border: `2px solid ${borderColor}`,
                                      }
                                    }
                                  >
                                    {Number.isFinite(
                                      seriesMetrics?.current_metrics
                                    ) &&
                                      seriesMetrics?.current_metrics >= 0 && (
                                        <span className="info-value">
                                          {seriesMetrics?.current_metrics?.toLocaleString()}
                                        </span>
                                      )}
                                  </span>
                                  {widgetDurationShowPercentChange ? (
                                    <span
                                      className={`text_regular d-flex justify-content-center align-items-center ${
                                        String(
                                          seriesMetrics?.text
                                        ).toLowerCase() ===
                                        String("Negative").toLowerCase()
                                          ? seriesMetrics?.percent_change > 0
                                            ? "negative"
                                            : seriesMetrics?.percent_change < 0
                                            ? "positive"
                                            : "text-gray"
                                          : seriesMetrics?.percent_change > 0
                                          ? "positive"
                                          : seriesMetrics?.percent_change < 0
                                          ? "negative"
                                          : "text-gray"
                                      } font-size12`}
                                    >
                                      {seriesMetrics?.percent_change ===
                                      null ? (
                                        ""
                                      ) : seriesMetrics?.percent_change > 0 ? (
                                        <>
                                          <CaretUpFilled className="font-size18" />
                                          {" " +
                                            Number(
                                              seriesMetrics?.percent_change
                                            )?.toLocaleString() +
                                            "%"}
                                        </>
                                      ) : seriesMetrics?.percent_change < 0 ? (
                                        <>
                                          <CaretDownFilled className="font-size18" />
                                          {" " +
                                            Number(
                                              seriesMetrics?.percent_change
                                            )?.toLocaleString() +
                                            "%"}
                                        </>
                                      ) : (
                                        seriesMetrics?.percent_change !==
                                          null && "NA"
                                      )}{" "}
                                    </span>
                                  ) : (
                                    ""
                                  )}
                                </span>
                              </div>
                              {widgetDurationShowData ? (
                                <div className="info-section">
                                  <span className="info-label">{`Prev. ${
                                    seriesMetrics?.trace_name
                                      ? seriesMetrics?.trace_name
                                      : seriesMetrics?.attribute
                                  }: `}</span>
                                  <span className="info-value-container">
                                    <span
                                      className="info-value-box"
                                      // className={`info-value-box ${
                                      //   checkBackgroundIsDark(fillColor)
                                      //     ? "info-dark-bg"
                                      //     : ""
                                      // }`}
                                      style={
                                        {
                                          // background: `linear-gradient(
                                          //   45deg,
                                          //   ${fillColor},
                                          //   ${fillColor}60
                                          // )`,
                                          // border: `2px solid ${borderColor}`,
                                        }
                                      }
                                    >
                                      {Number.isFinite(
                                        seriesMetrics?.previous_metrics
                                      ) &&
                                        seriesMetrics?.previous_metrics >=
                                          0 && (
                                          <span className="info-prev">
                                            {seriesMetrics?.previous_metrics?.toLocaleString()}
                                          </span>
                                        )}
                                    </span>
                                  </span>
                                </div>
                              ) : (
                                ""
                              )}
                            </>
                          );
                        }
                      )}
                    </>
                  ) : (
                    ""
                  )}
                  {/* Show Series Metrics Info End Here */}
                  {/* {Number.isFinite(selectedLocation?.current_metric) &&
                selectedLocation?.current_metric >= 0 ? (
                  <div className="info-section">
                    <span className="info-label">Latest Rating:</span>
                    <span
                      className={`info-value-box ${
                        checkBackgroundIsDark(getFillColor(selectedLocation))
                          ? "info-dark-bg"
                          : ""
                      }`}
                      style={{
                        background: `linear-gradient(
                            45deg,
                            ${getFillColor(selectedLocation)},
                            ${getFillColor(selectedLocation)}60
                          )`,
                        border: `2px solid ${getBorderColor(selectedLocation)}`,
                      }}
                    >
                      <span className="info-value">
                        {selectedLocation?.current_metric?.toLocaleString()}
                      </span>
                      {Number.isFinite(selectedLocation?.previous_metric) &&
                        selectedLocation?.previous_metric >= 0 && (
                          <span className="info-prev">
                            (Prev: {selectedLocation?.previous_metric?.toLocaleString()})
                          </span>
                        )}
                    </span>
                  </div>
                ) : (
                  ""
                )}
                {Number.isFinite(selectedLocation?.size) &&
                selectedLocation?.size >= 0 ? (
                  <div className="info-section">
                    <span className="info-label">Total Responses:</span>
                    <span
                      className={`info-value-box ${
                        checkBackgroundIsDark(getFillColor(selectedLocation))
                          ? "info-dark-bg"
                          : ""
                      }`}
                      style={{
                        background: `linear-gradient(
                            45deg,
                            ${getFillColor(selectedLocation)},
                            ${getFillColor(selectedLocation)}60
                          )`,
                        border: `2px solid ${getBorderColor(selectedLocation)}`,
                      }}
                    >
                      <span className="info-value">
                        {selectedLocation?.size?.toLocaleString()}
                      </span>
                      {Number.isFinite(selectedLocation?.previous_size) &&
                        selectedLocation?.previous_size >= 0 && (
                          <span className="info-prev">
                            (Prev: {selectedLocation?.previous_size?.toLocaleString()})
                          </span>
                        )}
                    </span>
                  </div>
                ) : (
                  ""
                )} */}
                  {/* {selectedLocation?.feedback ? (
                  <div className="info-section">
                    <span className="info-label">Feedback:</span>
                    <ul className="info-feedback-list">
                      {Object.entries(selectedLocation.feedback).map(
                        ([key, value]) => (
                          <li key={key}>
                            <span className="feedback-key">{key}:</span>{" "}
                            <span className="feedback-value">{value?.toLocaleString()}</span>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                ) : (
                  ""
                )} */}
                </div>

                {/* Show Rating or Breakdown Info Start Here */}
                {breakdownLegends?.length || thresholdLegends ? (
                  <div className="info-footer">
                    <div className="InfoWindow_Legends">
                      {thresholdLegends?.low ? (
                        <div className="InfoWindow_Legends--row">
                          {" "}
                          <span
                            className="map__legend"
                            style={{
                              backgroundColor: `${selectedLocation?.thresholdLegend?.color}1a`,
                              borderColor: `${selectedLocation?.thresholdLegend?.color}99`,
                            }}
                          >
                            <span
                              className="map__legend--color"
                              style={{
                                backgroundColor:
                                  selectedLocation?.thresholdLegend?.color,
                              }}
                            ></span>
                            <span className="map__legend--name">
                              {selectedLocation?.thresholdLegend?.name}
                            </span>
                          </span>
                        </div>
                      ) : (
                        ""
                      )}
                      {Array.isArray(breakdownLegends) &&
                      breakdownLegends?.length &&
                      selectedLocation?.breakdown_val ? (
                        <div className="InfoWindow_Legends--row">
                          <span
                            className="map__legend"
                            style={{
                              backgroundColor: `${selectedLocation?.breakdown_color}1a`,
                              borderColor: `${selectedLocation?.breakdown_color}99`,
                            }}
                          >
                            <span
                              className="map__legend--borderColor"
                              style={{
                                borderColor: selectedLocation?.breakdown_color,
                              }}
                            ></span>
                            <span className="map__legend--name">
                              {selectedLocation?.breakdown_val}
                            </span>
                          </span>
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </div>
                ) : (
                  ""
                )}
                {/* Show Rating or Breakdown Info End Here */}
              </div>
            </InfoWindow>
          </Map>
        </div>

        {breakdownLegends?.length || thresholdLegends ? (
          <div ref={legendContainerRef} className="GoogleMultiLayerMap_Legends">
            {Array.isArray(breakdownLegends) ? (
              <div className="GoogleMultiLayerMap_Legends--row">
                {breakdownLegends?.map((legend, index) => {
                  if (index < 10) {
                    return (
                      <span className="map__legend">
                        <span
                          className="map__legend--borderColor"
                          style={{ borderColor: legend?.color }}
                        ></span>
                        <span className="map__legend--name">
                          {legend?.name}
                        </span>
                      </span>
                    );
                  } else {
                    return "";
                  }
                })}
                {breakdownLegends?.length > 10 ? (
                  <Tooltip
                    overlayStyle={{
                      minWidth: "15rem",
                    }}
                    overlayInnerStyle={{
                      padding: "0.8rem",
                      borderRadius: "0.8rem",
                    }}
                    overlayClassName="GoogleMultiLayerMap__Tooltip"
                    title={
                      <div className="GoogleMultiLayerMap_Legends--tooltip">
                        {breakdownLegends?.map((legend, index) => {
                          if (index >= 10) {
                            return (
                              <span className="map__legend">
                                <span
                                  className="map__legend--borderColor"
                                  style={{ borderColor: legend?.color }}
                                ></span>
                                <span className="map__legend--name">
                                  {legend?.name}
                                </span>
                              </span>
                            );
                          } else {
                            return "";
                          }
                        })}
                      </div>
                    }
                    placement="top"
                  >
                    <span className="map__legend--count">
                      <span className="map__legend--countName">
                        +{breakdownLegends?.length - 10}
                      </span>
                    </span>
                  </Tooltip>
                ) : (
                  ""
                )}
              </div>
            ) : (
              ""
            )}
            {thresholdLegends?.low ? (
              <div className="GoogleMultiLayerMap_Legends--row">
                {" "}
                <span className="map__legend">
                  <span
                    className="map__legend--color"
                    style={{ backgroundColor: thresholdLegends?.low?.color }}
                  ></span>
                  <span className="map__legend--name">
                    {thresholdLegends?.low?.name}
                  </span>
                </span>
                <span className="map__legend">
                  <span
                    className="map__legend--color"
                    style={{ backgroundColor: thresholdLegends?.medium?.color }}
                  ></span>
                  <span className="map__legend--name">
                    {thresholdLegends?.medium?.name}
                  </span>
                </span>
                <span className="map__legend">
                  <span
                    className="map__legend--color"
                    style={{ backgroundColor: thresholdLegends?.high?.color }}
                  ></span>
                  <span className="map__legend--name">
                    {thresholdLegends?.high?.name}
                  </span>
                </span>
              </div>
            ) : (
              ""
            )}
          </div>
        ) : (
          ""
        )}
      </div>
    </>
  );
};

export default GoogleApiWrapper((props) => ({
  apiKey: "AIzaSyDGqipm4Lsoq2WkGQDoH1_Iu6eXPjiuE5A", // Replace with your API key
  //   apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY, // Secure API Key
  libraries: ["visualization"],
  language: props.language,
  // Use the region passed as a prop from the parent
  region: props.region || "IN",
}))(MapContainer);
